/**
 * Hero Column Widths Field Component
 * 
 * Allows users to adjust the relative widths of columns for the Hero component.
 * Supports adding and removing columns directly from the width editor.
 * 
 * NOTE: Adding/removing columns here updates the structural `hero_layout` (items per column)
 * in addition to the `hero_column_widths` (percentages).
 */
import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { InputNumber } from "antd";
import { MobileOutlined, TabletOutlined, LaptopOutlined, DesktopOutlined, PlusOutlined, CloseOutlined } from "@ant-design/icons";
import { deriveLayoutMode } from '../../utils/heroLayoutUtils';
import { EditContext } from "../../context/EditContext";
import InfoIcon from "../InfoIcon";
import ScreenSizeModeSelector from "./ScreenSizeModeSelector";
import { typography, surfaces, borders, textColors, interactive } from "../../../design-system/tokens";

function clamp(n, min, max) {
  if (typeof n !== "number" || isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function getColumnCount(heroLayout, breakpoint) {
  if (!heroLayout || !breakpoint) return 1;
  const raw = heroLayout[breakpoint];
  if (Array.isArray(raw) && raw.length > 0) return raw.length;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed.length;
    } catch {}
  }
  return 1;
}

// No viewport coupling here; this field follows the hero_layout linkage (one/two/all)

function normaliseTo100(numbers) {
  const safe = Array.isArray(numbers) ? numbers.map((v) => (typeof v === "number" && isFinite(v) ? v : 0)) : [];
  const total = safe.reduce((a, b) => a + b, 0);
  if (total <= 0) {
    const n = Math.max(safe.length, 1);
    const eq = 100 / n;
    const arr = Array.from({ length: n }, () => Number(eq.toFixed(2)));
    const drift = Number((100 - arr.reduce((a, b) => a + b, 0)).toFixed(2));
    if (arr.length) arr[arr.length - 1] = Number((arr[arr.length - 1] + drift).toFixed(2));
    return arr;
  }
  const scaled = safe.map((v) => (v / total) * 100);
  const rounded = scaled.map((v) => Number(v.toFixed(2)));
  const drift = Number((100 - rounded.reduce((a, b) => a + b, 0)).toFixed(2));
  if (rounded.length) rounded[rounded.length - 1] = Number((rounded[rounded.length - 1] + drift).toFixed(2));
  return rounded;
}

function adjustIndexKeepingSum(widths, index, newValue) {
  const n = widths.length;
  if (n === 0) return [];
  const minWidth = 10; // 10% minimum per column
  
  // Calculate maximum allowed width: 100% - (10% * number of other columns)
  const maxAllowed = 100 - (minWidth * (n - 1));
  
  const bounded = clamp(typeof newValue === "number" ? newValue : widths[index], minWidth, maxAllowed);
  const othersSum = widths.reduce((a, b, i) => a + (i === index ? 0 : b), 0);
  const remaining = Math.max(0, 100 - bounded);
  let next = widths.slice();
  next[index] = bounded;
  if (othersSum <= 0) {
    const per = n > 1 ? remaining / (n - 1) : 0;
    for (let i = 0; i < n; i++) if (i !== index) next[i] = Math.max(per, minWidth);
  } else {
    for (let i = 0; i < n; i++) {
      if (i !== index) {
        const proportional = (widths[i] / othersSum) * remaining;
        next[i] = Math.max(proportional, minWidth);
      }
    }
  }
  return normaliseTo100(next);
}

function MultiColumnBar({ widths, onChange, step = 0.1, min = 10 }) {
  const containerRef = useRef(null);
  const [dragIndex, setDragIndex] = useState(null);

  const handleDown = (idx) => (e) => {
    e.preventDefault();
    setDragIndex(idx);
  };

  useEffect(() => {
    if (dragIndex == null) return;
    const onMove = (e) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      let x = clamp(clientX - rect.left, 0, rect.width);
      const percent = (x / rect.width) * 100;
      
      // Calculate cumulative position up to dragIndex
      let cum = 0;
      for (let i = 0; i < dragIndex; i++) cum += widths[i];
      
      // The two adjacent columns can redistribute their combined width
      const combinedWidth = widths[dragIndex] + widths[dragIndex + 1];
      
      // Calculate new left column width (relative to its starting position)
      let newLeftWidth = percent - cum;
      
      // Constrain the left width to respect 10% minimum for both columns
      const minWidth = 10; // 10% minimum per column
      newLeftWidth = clamp(newLeftWidth, minWidth, combinedWidth - minWidth);
      
      // Right column gets the remainder of the combined width
      let newRightWidth = combinedWidth - newLeftWidth;
      
      // Apply step quantization
      const q = (v) => Number((Math.round(v / step) * step).toFixed(2));
      const leftQ = q(newLeftWidth);
      const rightQ = q(newRightWidth);
      
      // Ensure both columns respect the 10% minimum
      if (leftQ < minWidth || rightQ < minWidth) return;
      
      // Only update the two adjacent columns, leave all others unchanged
      const next = widths.slice();
      next[dragIndex] = leftQ;
      next[dragIndex + 1] = rightQ;
      
      // No normalization needed - we're just redistributing between two columns
      onChange(next);
    };
    const onUp = () => setDragIndex(null);
    window.addEventListener("mousemove", onMove, { passive: false });
    window.addEventListener("mouseup", onUp, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragIndex, widths, onChange, step, min]);

  const cumPositions = [];
  let acc = 0;
  for (let i = 0; i < widths.length - 1; i++) {
    acc += widths[i];
    cumPositions.push(acc);
  }

  return (
    <div className="w-full">
      <div ref={containerRef} className={`relative h-8 ${surfaces.tertiary} border ${borders.default} rounded overflow-hidden`}>
        <div className="absolute inset-0 flex">
          {widths.map((w, idx) => (
            <div 
              key={idx} 
              className={`h-full border-r ${borders.strong} last:border-r-0`}
              style={{ width: `${Math.max(w, 0)}%` }}
            >
              <div className={`h-full ${surfaces.active} ${surfaces.hover} transition-colors`} />
            </div>
          ))}
        </div>
        {cumPositions.map((p, i) => (
          <div
            key={i}
            className={`absolute top-0 h-full w-1 ${interactive.primaryBg} ${interactive.primaryHover} cursor-col-resize transition-colors z-10`}
            style={{ left: `calc(${p}% - 2px)` }}
            onMouseDown={handleDown(i)}
            onTouchStart={handleDown(i)}
          />
        ))}
      </div>
    </div>
  );
}

// Replaced per-column sliders with a single multi-separator bar and inline inputs

function SumIndicator({ sum }) {
  const ok = Math.abs(sum - 100) <= 0.5;
  const warn = Math.abs(sum - 100) > 0.5;
  return (
    <div className={`${typography.caption} ${ok ? textColors.secondary : warn ? textColors.tertiary : textColors.secondary}`}>
      Total: {Number.isFinite(sum) ? sum.toFixed(2) : "0.00"}% {warn ? "(Will be normalized on save)" : ""}
    </div>
  );
}

export default function HeroColumnWidthsField({ label, help, value, onChange, metadata, elementData, infoText }) {
  const { heroLayoutMode, updateElement, selectedElement } = React.useContext(EditContext);
  
  // Safety check for updateElement
  if (!updateElement) {
    console.error('HeroColumnWidthsField: updateElement is not available in EditContext');
  }

  // Determine display mode from the current hero_layout (one | two | all)
  const heroLayout = elementData?.hero_layout || {};
  const derived = useMemo(() => deriveLayoutMode(heroLayout), [heroLayout]);
  const [mode, setMode] = useState(heroLayoutMode || derived.mode);

  const min = 10; // 10% minimum per column
  const max = typeof metadata?.appearance_max === "number" ? metadata.appearance_max : 100;
  const step = typeof metadata?.appearance_step_size === "number" ? metadata.appearance_step_size : 0.1;

  // Helper: breakpoints groups according to two-mode semantics
  const MOBILE_BPS = ["extra_small", "small"];
  const DESKTOP_BPS = ["medium", "large", "extra_large"];

  // Compute representative breakpoints for groups
  const representativeMobileBP = "extra_small";
  const representativeDesktopBP = "medium";

  // Follow hero layout screen choice if provided
  useEffect(() => {
    if (heroLayoutMode && heroLayoutMode !== mode) setMode(heroLayoutMode);
  }, [heroLayoutMode]);

  const currentValues = value || {};

  // Utility: obtain widths array for a specific breakpoint and column count
  const getWidthsForBP = useCallback((bp, numCols) => {
    const raw = currentValues[bp];
    const n = Math.max(1, Number(numCols) || 1);

    // Default: equal widths
    const equal = Array.from({ length: n }, () => Number((100 / n).toFixed(2)));

    // If we have a widths array but it doesn't match the current column count (e.g., layout changed),
    // show equal widths instead of padding with 0s (prevents the "100 / 0" UI glitch).
    if (Array.isArray(raw) && raw.length > 0) {
      if (raw.length !== n) return equal;
      return raw.slice();
    }

    return equal;
  }, [currentValues]);

  // Utility: set widths for a list of breakpoints
  const setWidthsFor = useCallback((breakpoints, nextWidths) => {
    const updated = { ...(currentValues || {}) };
    breakpoints.forEach((bp) => {
      updated[bp] = nextWidths.slice();
    });
    onChange(updated);
  }, [onChange, currentValues]);

  // Handle adding a column: Updates hero_layout structure AND column widths
  const handleAddColumn = useCallback((breakpoints) => {
    if (!selectedElement) return;
    if (typeof updateElement !== "function") return;

    // Update hero_layout: Add 1 item capacity to the new column
    const currentLayout = selectedElement.hero_layout || {};
    const nextLayout = { ...currentLayout };
    
    // Update widths: Add equal share for new column
    const currentWidths = { ...currentValues };

    breakpoints.forEach(bp => {
        // 1. Update Layout Structure
        let layoutCols = [];
        try {
            const raw = currentLayout[bp];
            layoutCols = Array.isArray(raw) ? raw : JSON.parse(raw || "[6]");
        } catch {
            layoutCols = [6];
        }
        if (layoutCols.length < 6) {
            layoutCols.push(1); // Add new column with capacity 1
        }
        nextLayout[bp] = JSON.stringify(layoutCols);

        // 2. Update Widths
        const oldWidths = getWidthsForBP(bp, layoutCols.length - 1);
        const newCount = layoutCols.length;
        const equalShare = Number((100 / newCount).toFixed(2));
        // Rescale old widths to fit in (100 - equalShare) space
        const scaleFactor = (100 - equalShare) / 100;
        const newWidths = oldWidths.map(w => Number((w * scaleFactor).toFixed(2)));
        newWidths.push(equalShare);
        
        // Fix drift
        const sum = newWidths.reduce((a, b) => a + b, 0);
        const drift = 100 - sum;
        newWidths[newWidths.length - 1] = Number((newWidths[newWidths.length - 1] + drift).toFixed(2));
        
        currentWidths[bp] = newWidths;
    });

    // Dispatch a SINGLE update so autosave doesn't overwrite one field with another
    updateElement({
      hero_layout: nextLayout,
      hero_column_widths: currentWidths,
    });

  }, [selectedElement, updateElement, currentValues, getWidthsForBP]);

  // Handle removing a column: Updates hero_layout structure AND column widths
  const handleRemoveColumn = useCallback((breakpoints, indexToRemove) => {
    if (!selectedElement) return;
    if (typeof updateElement !== "function") return;

    // Update hero_layout: Remove column at index
    const currentLayout = selectedElement.hero_layout || {};
    const nextLayout = { ...currentLayout };
    
    // Update widths: Remove width at index and redistribute
    const currentWidths = { ...currentValues };

    breakpoints.forEach(bp => {
        // 1. Update Layout Structure
        let layoutCols = [];
        try {
            const raw = currentLayout[bp];
            layoutCols = Array.isArray(raw) ? raw : JSON.parse(raw || "[6]");
        } catch {
            layoutCols = [6];
        }
        
        if (layoutCols.length > 1) {
            // Remove column at index
            layoutCols.splice(indexToRemove, 1);
            nextLayout[bp] = JSON.stringify(layoutCols);

            // 2. Update Widths
            const oldWidths = getWidthsForBP(bp, layoutCols.length + 1);
            const removedWidth = oldWidths[indexToRemove];
            const remainingWidths = oldWidths.filter((_, i) => i !== indexToRemove);
            
            // Distribute removed width proportionally among remaining
            const remainingSum = remainingWidths.reduce((a, b) => a + b, 0);
            let newWidths = [];
            
            if (remainingSum > 0) {
                newWidths = remainingWidths.map(w => {
                    const share = (w / remainingSum) * removedWidth;
                    return Number((w + share).toFixed(2));
                });
            } else {
                 // Fallback if remaining sum is 0 (unlikely)
                 newWidths = remainingWidths.map(() => 0);
            }

            // Fix drift
            const sum = newWidths.reduce((a, b) => a + b, 0);
            if (newWidths.length > 0) {
                const drift = 100 - sum;
                newWidths[newWidths.length - 1] = Number((newWidths[newWidths.length - 1] + drift).toFixed(2));
            }

            currentWidths[bp] = newWidths;
        }
    });

    // Dispatch a SINGLE update so autosave doesn't overwrite one field with another
    updateElement({
      hero_layout: nextLayout,
      hero_column_widths: currentWidths,
    });

  }, [selectedElement, updateElement, currentValues, getWidthsForBP]);


  // Group-specific helpers
  const numColumnsFor = useCallback((bp) => getColumnCount(heroLayout, bp), [heroLayout]);

  // Prepare data for each mode
  const oneBP = representativeDesktopBP; // use desktop as representative for unified editing
  const oneNumCols = numColumnsFor(oneBP);
  const oneWidths = getWidthsForBP(oneBP, oneNumCols);
  const oneSum = oneWidths.reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);

  const mobNumCols = numColumnsFor(representativeMobileBP);
  const mobWidths = getWidthsForBP(representativeMobileBP, mobNumCols);
  const mobSum = mobWidths.reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);

  const deskNumCols = numColumnsFor(representativeDesktopBP);
  const deskWidths = getWidthsForBP(representativeDesktopBP, deskNumCols);
  const deskSum = deskWidths.reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);

  // Equalize helpers per group
  const equalizeOne = useCallback(() => {
    const eq = Array.from({ length: oneNumCols }, () => Number((100 / Math.max(oneNumCols, 1)).toFixed(2)));
    setWidthsFor(["extra_small", "small", "medium", "large", "extra_large"], eq);
  }, [oneNumCols, setWidthsFor]);

  const equalizeMobile = useCallback(() => {
    const eq = Array.from({ length: mobNumCols }, () => Number((100 / Math.max(mobNumCols, 1)).toFixed(2)));
    setWidthsFor(MOBILE_BPS, eq);
  }, [mobNumCols, setWidthsFor]);

  const equalizeDesktop = useCallback(() => {
    const eq = Array.from({ length: deskNumCols }, () => Number((100 / Math.max(deskNumCols, 1)).toFixed(2)));
    setWidthsFor(DESKTOP_BPS, eq);
  }, [deskNumCols, setWidthsFor]);

  // Update handlers per group
  const updateOneAtIndex = useCallback((idx, newVal) => {
    const next = adjustIndexKeepingSum(oneWidths, idx, newVal);
    setWidthsFor(["extra_small", "small", "medium", "large", "extra_large"], next);
  }, [oneWidths, setWidthsFor]);

  const updateMobileAtIndex = useCallback((idx, newVal) => {
    const next = adjustIndexKeepingSum(mobWidths, idx, newVal);
    setWidthsFor(MOBILE_BPS, next);
  }, [mobWidths, setWidthsFor]);

  const updateDesktopAtIndex = useCallback((idx, newVal) => {
    const next = adjustIndexKeepingSum(deskWidths, idx, newVal);
    setWidthsFor(DESKTOP_BPS, next);
  }, [deskWidths, setWidthsFor]);

  const unifyAll = useCallback(() => setMode("one"), []);
  const collapseToTwo = useCallback(() => setMode("two"), []);
  const expandToAll = useCallback(() => setMode("all"), []);

  const handleModeChange = useCallback((newMode) => {
    if (newMode === "one") unifyAll();
    else if (newMode === "two") collapseToTwo();
    else if (newMode === "all") expandToAll();
  }, [unifyAll, collapseToTwo, expandToAll]);

  const renderBreakpointsLegend = () => (
    <div className={`grid grid-cols-5 gap-2 ${typography.caption}`}>
      <div className="flex items-center gap-1"><MobileOutlined /> XS</div>
      <div className="flex items-center gap-1"><TabletOutlined /> S</div>
      <div className="flex items-center gap-1"><LaptopOutlined /> M</div>
      <div className="flex items-center gap-1"><DesktopOutlined /> L</div>
      <div className="flex items-center gap-1"><DesktopOutlined /> XL</div>
    </div>
  );

  return (
    <div className="field-wrapper">
      {label && (
        <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
          {label}
          {infoText ? <span className="ml-2 align-middle"><InfoIcon text={infoText} /></span> : null}
        </label>
      )}
      {help && <div className={`mb-2 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
      <ScreenSizeModeSelector
        mode={mode}
        onChange={handleModeChange}
        disabled={false}
      />
      <div className="space-y-3">

      {mode === "one" && (
        <div className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {oneWidths.map((v, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className={`${typography.caption} ${textColors.primary}`}>Col {i + 1}</span>
                <InputNumber size="small" value={Number(v.toFixed(2))} min={min} max={max} step={step} className="w-24" onChange={(nv) => updateOneAtIndex(i, typeof nv === "number" ? nv : v)} />
                <button 
                  type="button" 
                  onClick={() => handleRemoveColumn(["extra_small", "small", "medium", "large", "extra_large"], i)}
                  className={`${textColors.muted} ${textColors.hover} disabled:opacity-30 disabled:cursor-not-allowed transition-colors p-1`}
                  disabled={oneNumCols <= 1}
                  title="Remove column"
                >
                  <CloseOutlined />
                </button>
              </div>
            ))}
          </div>
          <MultiColumnBar widths={oneWidths} onChange={(next) => setWidthsFor(["extra_small", "small", "medium", "large", "extra_large"], next)} step={step} min={min} />
          <div className="flex items-center justify-between">
            <SumIndicator sum={oneSum} />
            <div className="flex gap-2">
                <button type="button" className={`px-2 py-1 border ${borders.default} rounded ${typography.caption} ${surfaces.primary} ${textColors.primary} ${surfaces.hover}`} onClick={() => handleAddColumn(["extra_small", "small", "medium", "large", "extra_large"])} title="Add column"><PlusOutlined /> Add</button>
                <button type="button" className={`px-2 py-1 border ${borders.default} rounded ${typography.caption} ${surfaces.primary} ${textColors.primary} ${surfaces.hover}`} onClick={equalizeOne} title="Make columns equal">Make equal</button>
            </div>
          </div>
        </div>
      )}

      {mode === "two" && (
        <div className="space-y-4">
          <div>
            <div className={`flex items-center gap-2 ${typography.bodySmall} font-medium ${textColors.primary} mb-1`}><MobileOutlined /> Phone</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-2">
              {mobWidths.map((v, i) => (
                <div key={`mob-${i}`} className="flex items-center gap-2">
                  <span className={`${typography.caption} ${textColors.primary}`}>Col {i + 1}</span>
                  <InputNumber size="small" value={Number(v.toFixed(2))} min={min} max={max} step={step} className="w-24" onChange={(nv) => updateMobileAtIndex(i, typeof nv === "number" ? nv : v)} />
                  <button 
                    type="button" 
                    onClick={() => handleRemoveColumn(MOBILE_BPS, i)}
                    className={`${textColors.muted} ${textColors.hover} disabled:opacity-30 disabled:cursor-not-allowed transition-colors p-1`}
                    disabled={mobNumCols <= 1}
                    title="Remove column"
                    >
                    <CloseOutlined />
                    </button>
                </div>
              ))}
            </div>
            <MultiColumnBar widths={mobWidths} onChange={(next) => setWidthsFor(MOBILE_BPS, next)} step={step} min={min} />
            <div className="flex items-center justify-between mt-1">
              <SumIndicator sum={mobSum} />
              <div className="flex gap-2">
                <button type="button" className={`px-2 py-1 border ${borders.default} rounded ${typography.caption} ${surfaces.primary} ${textColors.primary} ${surfaces.hover}`} onClick={() => handleAddColumn(MOBILE_BPS)} title="Add column"><PlusOutlined /> Add</button>
                <button type="button" className={`px-2 py-1 border ${borders.default} rounded ${typography.caption} ${surfaces.primary} ${textColors.primary} ${surfaces.hover}`} onClick={equalizeMobile} title="Make columns equal">Make equal</button>
              </div>
            </div>
          </div>

          <div>
            <div className={`flex items-center gap-2 ${typography.bodySmall} font-medium ${textColors.primary} mb-1`}><LaptopOutlined /> Desktop</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-2">
              {deskWidths.map((v, i) => (
                <div key={`desk-${i}`} className="flex items-center gap-2">
                  <span className={`${typography.caption} ${textColors.primary}`}>Col {i + 1}</span>
                  <InputNumber size="small" value={Number(v.toFixed(2))} min={min} max={max} step={step} className="w-24" onChange={(nv) => updateDesktopAtIndex(i, typeof nv === "number" ? nv : v)} />
                   <button 
                    type="button" 
                    onClick={() => handleRemoveColumn(DESKTOP_BPS, i)}
                    className={`${textColors.muted} ${textColors.hover} disabled:opacity-30 disabled:cursor-not-allowed transition-colors p-1`}
                    disabled={deskNumCols <= 1}
                    title="Remove column"
                    >
                    <CloseOutlined />
                    </button>
                </div>
              ))}
            </div>
            <MultiColumnBar widths={deskWidths} onChange={(next) => setWidthsFor(DESKTOP_BPS, next)} step={step} min={min} />
            <div className="flex items-center justify-between mt-1">
              <SumIndicator sum={deskSum} />
              <div className="flex gap-2">
                <button type="button" className={`px-2 py-1 border ${borders.default} rounded ${typography.caption} ${surfaces.primary} ${textColors.primary} ${surfaces.hover}`} onClick={() => handleAddColumn(DESKTOP_BPS)} title="Add column"><PlusOutlined /> Add</button>
                <button type="button" className={`px-2 py-1 border ${borders.default} rounded ${typography.caption} ${surfaces.primary} ${textColors.primary} ${surfaces.hover}`} onClick={equalizeDesktop} title="Make columns equal">Make equal</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {mode === "all" && (
        <div className="space-y-4">
          {(["extra_small", "small", "medium", "large", "extra_large"]).map((bp) => {
            const labelMap = {
              extra_small: (<span className="flex items-center gap-1"><MobileOutlined /> XS</span>),
              small: (<span className="flex items-center gap-1"><TabletOutlined /> S</span>),
              medium: (<span className="flex items-center gap-1"><LaptopOutlined /> M</span>),
              large: (<span className="flex items-center gap-1"><DesktopOutlined /> L</span>),
              extra_large: (<span className="flex items-center gap-1"><DesktopOutlined /> XL</span>)
            };
            const n = numColumnsFor(bp);
            const w = getWidthsForBP(bp, n);
            const s = w.reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
            const eq = () => {
              const arr = Array.from({ length: n }, () => Number((100 / Math.max(n, 1)).toFixed(2)));
              setWidthsFor([bp], arr);
            };
            const updateIdx = (idx, nv) => {
              const next = adjustIndexKeepingSum(w, idx, nv);
              setWidthsFor([bp], next);
            };
            return (
              <div key={bp} className="space-y-2">
                <div className={`${typography.bodySmall} font-medium ${textColors.primary}`}>{labelMap[bp]}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {w.map((v, i) => (
                    <div key={`${bp}-${i}`} className="flex items-center gap-2">
                      <span className={`${typography.caption} ${textColors.primary}`}>Col {i + 1}</span>
                      <InputNumber size="small" value={Number(v.toFixed(2))} min={min} max={max} step={step} className="w-24" onChange={(nv) => updateIdx(i, nv)} />
                      <button 
                        type="button" 
                        onClick={() => handleRemoveColumn([bp], i)}
                        className={`${textColors.muted} ${textColors.hover} disabled:opacity-30 disabled:cursor-not-allowed transition-colors p-1`}
                        disabled={n <= 1}
                        title="Remove column"
                        >
                        <CloseOutlined />
                        </button>
                    </div>
                  ))}
                </div>
                <MultiColumnBar widths={w} onChange={(next) => setWidthsFor([bp], next)} step={step} min={min} />
                <div className="flex items-center justify-between">
                  <SumIndicator sum={s} />
                  <div className="flex gap-2">
                    <button type="button" className={`px-2 py-1 border ${borders.default} rounded ${typography.caption} ${surfaces.primary} ${textColors.primary} ${surfaces.hover}`} onClick={() => handleAddColumn([bp])} title="Add column"><PlusOutlined /> Add</button>
                    <button type="button" className={`px-2 py-1 border ${borders.default} rounded ${typography.caption} ${surfaces.primary} ${textColors.primary} ${surfaces.hover}`} onClick={eq} title="Make columns equal">Make equal</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className={`${typography.caption} ${textColors.tertiary}`}>Widths are normalized to 100% on save.</div>
      </div>
    </div>
  );
}
